import type { AudioManager } from '@mimir/web/components/Common/Timeline/Audio/audioManager';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { TimeRange } from '@mimir/common/util/timeRange';
import { ActiveBuffering } from '@mimir/web/mse/activeBuffering';
import { invariant } from '@mimir/common/util/invariant';
import { exceptionToString } from '@mimir/common/util/exception';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import type { MarkPoint } from '@mimir/client-common/timeline/Timeline';
import { preferLowBandwidth, preferQuality } from '@mimir/web/mse/streamPreference';
import { getFramePtsConversion } from '@mimir/client-common/model/playableItem';
import type { FramePtsConversion, FrameTime} from '@mimir/client-common/time/frameTime';
import { frameTimeToPts, sourceMediaPositionToPts, ptsToFrameTime, browserCurrentTimeToPts, frameTimeToSourceMediaPosition } from '@mimir/client-common/time/frameTime';
import { getBehaviorFromBrowserEnvironment } from '@mimir/client-common/util/browserBehavior';
import type { FuzzyValue} from '@mimir/common/util/fuzzyNumbers';
import { addFuzzy, compareFuzzy, fractionToFuzzy, integerToFuzzy, minFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import type { MediaSelection } from '@mimir/common/util/timeRange';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { OptimizedSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import { avoidCORSConflict } from '@mimir/web/util/thumbnail';
import { createSeparateAudioProxiesAudioManager } from '@mimir/web/components/Common/Timeline/Audio/separateAudioProxiesAudioManager';
import { type AudioVisualizationState, type ItemMediaPlayerOps, type PlayerError, type PostLoadOperation, type AudioInitializationCallbacks, NoOpM4aProxiesCallback } from '@mimir/web/model/itemMediaPlayerOps';
import { ObservableState, type Observable, calculatedObservable } from '@mimir/common/util/observable';
import { updateAudioTrackConfig, type AudioTrackConfig, type AudioTrackConfigUpdate, type PeakRange, createMixMatrixFromAudioTrackConfig } from './audioTrackConfig';
import type { Cleanable } from '@mimir/common/util/cleanable';
import { createUuid } from '@mimir/common/util/createUuid';
import type { AudioDownMix } from '@mimir/common/dto/audioMixingDtos';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import type { PlayableArtifact, PlayableArtifactSelectOptions, PlayableArtifactStream, PlayableArtifactTams, PlayableItemDto, PlayableItemGetOptions, VirtualClipInfo } from '@mimir/common/dto/playableItemDto';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import { FRAC_HALF, FRAC_ZERO, type Fraction } from '@mimir/common/util/fractionType';
import { ConsoleLogger } from '../../../../common/src/util/logging';
import { approximateFractionValue, frac } from '@mimir/common/util/integerMath';
import type { CmafSegmentSource} from '@mimir/client-common/mse/mseBuffering';
import { startMseBufferingAndReturnUrl } from '@mimir/client-common/mse/mseBuffering';
import type { TsAudioController } from '@mimir/client-common/mse/mimirStreamRoleCmafSegmentSource';
import { createCmafSegmentSourcesForItem } from '@mimir/client-common/mse/mimirStreamRoleCmafSegmentSource';
import { createTamsCmafSegmentSource } from '@mimir/client-common/mse/tamsCmafSegmentSource';
import cloneDeep from 'lodash/cloneDeep';
import type { MediaSourceOps, MediaSourceProviderOps, SourceBufferOps } from '@mimir/client-common/mse/mediaSourceOps';
import { createWebWorkerAsyncTransmuxer } from '../mse/webWorkerAsyncTransmuxer';
import { createDefaultTamsClient } from '@mimir/common/tams/defaultTamsClient';
import { mimirDevEnabled } from '../util/localStorage';
import { inspect } from '@mimir/common/mpegts/inspect';
import type { PlaybackQuality, QualityChangeState, QualityOption, QualitySelectionResult } from './playbackQuality';
import { detectQualityFromArtifact, getAvailableQualitiesFromItem, getLastSelectedQuality, selectQualityForLoad } from './playbackQuality';
import type { SubtitleCue } from '@mimir/common/types/subtitleCue';
import type { VisualizationMode } from '@mimir/browser/cutter/audioVisualizer';
import type { AudioLevelsData } from '@mimir/cutter/player/cutterCorePlayer';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import type { PeaksManagerOps } from './peaksManagerOps';
import { loadPeaks } from '../pages/cutter/loadPeaks';

declare global {
  interface HTMLMediaElement {
    controlsList: DOMTokenList
  }
}

export type Cleaned = () => void;

export interface VideoPlayerOptions {
  id?: string;
  useEditProxy?: boolean;
  useAnimationLoop?: boolean;

  /**
   *  Interval at which to poll the video player for updates in the cached
   *  buffer ranges. If not set then no polling will occur outside of events
   *  received from the player.
   */
  pollBufferedIntervalMs?: number;

  /**
   *  Maximum amount of buffered media when using MediaSourceExtension. If set
   *  to undefined then max buffered duration will be limited by the browser
   *  hard limit.
   */
  maxBufferedDuration?: number,
}

/**
 *  This interface holds all the relevant objects for an ongoing streaming
 *  playback.
 */
interface StreamingPlaybackState {
  /**
   *  Things to cleanup the current live streaming
   */
  cleanables: Cleanable[],

  /**
   *  Update the audio track config (mute/solo state)
   */
  updateAudioTrackConfig: (update: AudioTrackConfigUpdate[]) => void,

  /**
   *  Get media source extension object URL
   */
  getMediaUrl: () => string,

  /**
   *  Initiate more buffering.
   *
   *  TODO: We should remove this once we no longer use ActiveBuffering since
   *  the buffering logic should itself be responsible for knowing when to
   *  buffer more.
   */
   bufferMore: () => void,
}

export class MarkPointsPreview {
  public visible = false;
  public type: MarkPoint | null = null;

  private _images: Record<MarkPoint, string | null>;
  private _mss: Record<MarkPoint, FuzzyValue | null>;

  private player: ItemMediaPlayerOps;

  constructor(player: ItemMediaPlayerOps) {
    this.player = player;
    this._images = { 'mark-in': null, 'mark-out': null };
    this._mss = { 'mark-in': null, 'mark-out': null };
  }

  async grab(type: MarkPoint, sourcePos: FuzzyValue) {
    this._images[type] = null; // clear to not show stale image

    this._mss[type] = sourcePos;
    void this.player.seekAndGrabImage(sourcePos)
      .then(img => {
        if (compareFuzzy(this._mss[type]!, sourcePos) !== 0) return; // ignore grabbed image if there is a new request
        this._images[type] = img;
      });
  }

  async tryGrabMarkPointFrame(type: MarkPoint, timeline: Timeline | undefined) {
    const virtualClipRange = this.player.getVirtualClipSourceMediaRangeObs().currentValue();
    const sampleDuration = this.player.getSampleDuration();
    const mediaPos = type === 'mark-in' ? timeline?.sourceInPosition : timeline?.getStartOfLastSelectedSample(virtualClipRange, sampleDuration);
    if (mediaPos != null) {
      return this.grab(type, mediaPos);
    }
  }


  get(type: MarkPoint): [FuzzyValue | null, string | null] {
    return [this._mss[type], this._images[type]];
  }
}

/**
 *  Wrapper object around the audio manager. We do this in order to protect
 *  ourselves from multiple loadAudio requests interrupting each others
 *  loading. By always creating a new state we make sure that only the last
 *  loadAudio call will affect the current audioManager.
 */
interface AudioManagerState {
  audioManager: AudioManager | null,
}


/**
 * This is the public way to create a video player, so that we don't
 * expose the concrete class but just the interface.
 */
export function createItemMediaVideoPlayer(
  services: {
    apiActions: VideoApiActions,
    fetch: BasicFetch,
    itemPollingOps: ItemPollingOps,
  },
  options: VideoPlayerOptions,
): ItemMediaPlayerOps {
  return new ItemMediaVideoPlayer(services, options);
}

type HtmlVideoElementEventType =
  'resize' |
  'play' |
  'pause' |
  'timeupdate' |
  'durationchange' |
  'canplay' |
  'seeking' |
  'seeked' |
  'loadeddata' |
  'loadedmetadata' |
  'loadstart' |
  'seeked' |
  'waiting' |
  'playing' |
  'error' |
  'progress';

/**
 * A class encapsulating functionality surrounding a HTML5 video player.
 *
 * This makes sure we have one source of truth that various components react to.
 */
class ItemMediaVideoPlayer implements ItemMediaPlayerOps {

  private paused = new ObservableState<boolean>(true);
  private visible = new ObservableState<boolean>(false);
  private hasMedia = new ObservableState<boolean>(false);
  private loading = new ObservableState<boolean>(false);
  private durationInMs = new ObservableState<number>(0);
  private sourceMediaDuration = new ObservableState<FuzzyValue>(fractionToFuzzy(frac(0, 1)));
  private virtualClipDuration : Observable<FuzzyValue>;
  private hasAudioChannels = new ObservableState<boolean>(false);
  private videoAspectRatio = new ObservableState<number | null>(null);
  private playerAspectRatio = new ObservableState<number | null>(null);


  private seeking = new ObservableState<boolean>(false);

  // If seeking fails we have this fail safe setTimeout to revert seeking to
  // false if there hasn't been a seeked event in 1 second. This is in order
  // for player to never get in a stuck state where seeking is not possible.
  // This bad state tends to happen if you request seeking outside of the area
  // that the media source can actually provide data for.
  private seekFailSafe : ReturnType<typeof setTimeout> | null = null;

  private framePtsConversion = new ObservableState<FramePtsConversion | null>(null);
  private lastError = new ObservableState<ErrorEvent | null>(null);
  private isLive = false;
  private hasGrowingMedia = new ObservableState<boolean>(false);
  private buffered = new ObservableState<TimeRange[]>([]);

  // These properties are passed down to the ActiveBuffering object
  // when we need to create one, so that it can update this state
  private bufferingBlocker = new ObservableState<string | null>(null);
  private bufferingTracking = new ObservableState<string | null>(null);
  private bufferingDebug = new ObservableState<string | null>(null);
  private audioTrackConfigs = new ObservableState<AudioTrackConfig[]>([]);
  private audioTrackCount = new ObservableState<number>(0);

  private streamingPlaybackState : StreamingPlaybackState | null = null;

  private itemId: string | null = null;
  private item: PlayableItemDto | null = null;

  private defaultLeftChannels = new Set<number>();
  private defaultRightChannels = new Set<number>();
  private audioManagerState: AudioManagerState = { audioManager: null, };
  private player: HTMLVideoElement;

  // Calculated observables
  private currentTimeInMs : Observable<number>;
  private currentFrameTime : Observable<FrameTime | null>;
  private sourceMediaPosition : Observable<FuzzyValue | null>;
  private virtualClipMediaPosition : Observable<FuzzyValue>;
  private virtualClipStart : Observable<FuzzyValue | null>;
  private virtualClipSourceMediaRange : Observable<MediaSelection>;
  private periodicBufferedUpdateTimeoutId : ReturnType<typeof setTimeout> | null = null;

  /*
    The same value as this.player.currentTime when the player has media, except
    this property is reactive, and therefore can be use Vue code that
    automatically want to rerender parts of the UI when the currentTime value
    changes.

    This is a raw HTMLVideoElement currentTime value. See
    https://github.com/Mjoll/mimir/wiki/Media-Time-Values for how this relates
    to other types of media time values.
  */
  private currentTime = new ObservableState<number | null>(null);
  private playerDuration = new ObservableState<number>(0.0);

  private playerWaiting = false;
  private vttUrl: string | null = null;
  private languageCode = 'en';

  // Explicit tracking of muted state, needed for quality switching when using AudioManager
  // because the AudioManager controls audio via volume (0 = muted) rather than the player's
  // native muted property.
  private explicitMutedState = false;

  private playerErrorCallbacks: PlayerError[] = [];
  private animationFrameRequested = false;

  private startOffsetInMs = new ObservableState<number>(0);

  private endOffsetInMs = new ObservableState(0);
  private ignoreNextError = false;

  // Don't push directly to this array. Use
  // postLoadOperations() instead to avoid having
  // operations never executed if they are scheduled after
  // the media has already been loaded
  private postLoadOperations : (() => void)[] = [];

  private pollListenerIds: string[] = [];

  private static calculateCurrentFrameTime(
    currentTime: number | null,
    framePtsConversion: FramePtsConversion | null
  ): FrameTime | null {
    if (framePtsConversion === null) {
      return null;
    }

    const pts = browserCurrentTimeToPts(currentTime ?? 0);
    return ptsToFrameTime(pts, framePtsConversion);
  }

  /*
    A value to apply to the HTMLVideoElement currentTime property as soon as the
    video element ready to seek and have completed any previous seek operations.

    This is a raw HTMLVideoElement currentTime value. See
    https://github.com/Mjoll/mimir/wiki/Media-Time-Values for how this relates
    to other types of media time values.
  */
  private nextCurrentTime = new ObservableState<number | null>(null);

  private selectArtifactOptions: PlayableArtifactSelectOptions;
  private useAnimationLoop: boolean;
  private currentArtifact: PlayableArtifact | null = null;

  private cleanables : Cleanable[] = [];

  private peaksManager: PeaksManagerOps | null = null;
  private peakCount = new ObservableState<number>(0);

  public id: string;

  private maxBufferedDuration : FuzzyValue | null;

  constructor(
    private services: {
      apiActions: VideoApiActions,
      fetch: BasicFetch,
      itemPollingOps: ItemPollingOps,
    },
    options: VideoPlayerOptions,
  ) {
    this.id = options.id ?? createUuid();
    this.selectArtifactOptions = {
      useEditProxy: options.useEditProxy ?? false,
      streamSetPreference: options.useEditProxy ? preferQuality : preferLowBandwidth,
    };
    this.useAnimationLoop = options.useAnimationLoop ?? true;
    const videoEl = window.document.createElement('video');
    videoEl.disableRemotePlayback = true;
    videoEl.crossOrigin = 'anonymous';
    videoEl.playsInline = true;
    videoEl.onclick = (e) => {
      e.preventDefault();
      this.togglePlayPause();
    };
    if (videoEl.controlsList) videoEl.controlsList.value = 'nodownload';
    this.player = videoEl;
    this.hookListeners();

    this.maxBufferedDuration =
      (options.maxBufferedDuration !== undefined) ? {
        approximate: options.maxBufferedDuration,
      } : null;


    this.currentTimeInMs =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateCurrentTimeInMs,
        this.cleanables,
        this.currentTime,
        this.nextCurrentTime,
        this.startOffsetInMs,
      );

    this.currentFrameTime =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateCurrentFrameTime,
        this.cleanables,
        this.currentTime,
        this.framePtsConversion,
      );

    this.sourceMediaPosition =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateSourceMediaPosition,
        this.cleanables,
        this.currentTime,
        this.nextCurrentTime,
        this.framePtsConversion,
      );

    this.virtualClipMediaPosition =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateVirtualClipMediaPosition,
        this.cleanables,
        this.sourceMediaPosition,
        this.startOffsetInMs,
      );

    this.virtualClipStart =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateVirtualClipStart,
        this.cleanables,
        this.startOffsetInMs,
        this.framePtsConversion,
      );

    this.virtualClipSourceMediaRange =
      calculatedObservable(
        ItemMediaVideoPlayer.calculateVirtualClipSourceMediaRange,
        this.cleanables,
        this.startOffsetInMs,
        this.endOffsetInMs,
        this.virtualClipStart,
        this.framePtsConversion,
        this.playerDuration,
      );

    this.virtualClipDuration =
      calculatedObservable(
        (virtualClipRange: MediaSelection, sourceMediaDuration: FuzzyValue): FuzzyValue => {
          if (virtualClipRange.whole) {
            return sourceMediaDuration;
          } else {
            const clampedEnd = minFuzzy(sourceMediaDuration, virtualClipRange.bounds.end);
            return subtractFuzzy(clampedEnd, virtualClipRange.bounds.start);
          }
        },
        this.cleanables,
        this.virtualClipSourceMediaRange,
        this.sourceMediaDuration,
      );

    if (options.pollBufferedIntervalMs !== undefined) {
      // Ideally we could just update the buffered ranges when the browser
      // emits a 'progress' event which is suppose to be fired every time the
      // browser load something. However this does not seem to be enough so we
      // just check for changes in the buffered ranges ever 500ms.
      const periodicBufferedUpdate = () => {
        this.updateBuffered();
        this.periodicBufferedUpdateTimeoutId = setTimeout(periodicBufferedUpdate, options.pollBufferedIntervalMs);
      };

      periodicBufferedUpdate();
    }

    this.audioTrackConfigs.observe(newValue => this.audioTrackCount.value = newValue.length);
  }


  /**
   * This is a nominal value, meaning that it reflects the position we want the
   * player to be in, not the position which the player actually is in. The
   * nominal and actual values differ during seeking.
   */
  private static calculateCurrentTimeInMs(
    currentTime: number | null,
    nextCurrentTime: number | null,
    startOffsetInMs: number
  ) {
    if (nextCurrentTime == null) {
      return ItemMediaVideoPlayer.calculateRoughVirtualMediaPosition(currentTime, startOffsetInMs);
    }

    return ItemMediaVideoPlayer.playerPtsToRoughVirtualMediaPosition(nextCurrentTime, startOffsetInMs);
  }

  public getPausedObs() {
    return this.paused.getObservable();
  }

  public getVisibleObs() {
    return this.visible.getObservable();
  }

  public getHasMediaObs() {
    return this.hasMedia.getObservable();
  }


  /**
   * Attempt to load the given audio proxies. Will return true if loaded, false if not.
   */
  public loadAudio(callbacks: AudioInitializationCallbacks): void {
    if (!this.item) {
      return;
    }

    if ((this.audioTrackConfigs.currentValue().length > 0)) {
      // If we already have ts audio, no need to load individual audio channels
      // if the flac downmix feature flag is enabled. If the flag is not enabled,
      // we want to keep going and possibly invoke createSeparateAudioProxiesAudioManager()
      // if the item has individual audio proxies
      callbacks.showPeaks();
      return;
    }

    const urls = this.item.proxyAudioTrackUrls;

    if (!urls)  {
      this.player.muted = false;
      this.hasAudioChannels.value = false;
      callbacks.onError('No peaks');
      return;
    }

    // Make a new AudioManagerState so that ongoing load audio requests
    // will not interfere with the latest audioManager
    const state : AudioManagerState = { audioManager: null, };
    this.cleanupAudioManager();
    this.audioManagerState = state;

    createSeparateAudioProxiesAudioManager(this.audioTrackConfigs, urls, this.defaultLeftChannels, this.defaultRightChannels, callbacks).then((audioManager) => {
      this.peakCount.value = audioManager.getPeakCount();
      audioManager.setVolume(this.player.muted ? 0 : this.getVolume());
      state.audioManager = audioManager;
      this.hasAudioChannels.value = true;
      this.player.muted = true;
      return true;
    })
    .catch((err: any) => {
      console.error(`Failed to load separate audio proxies audio manager: ${exceptionToString(err)}`);
      this.player.muted = false;
      this.hasAudioChannels.value = false;
      callbacks.onError(err);
      return false;
    });
  }

  /**
   * Updates the sets containing the indices of the tracks to play to left
   * and right by default, using the webProxyAudioDownMixInfo configuration
   * of the current item, if any.
   *
   * We can use this information to make sure that when showing all audio channels,
   * we use the same channels that were used during the creation of the web proxy
   * downmixed version.
   */
  private updateDefaultLeftAndRightChannels(webProxyAudioDownMixInfo: AudioDownMix | null) {
    this.defaultLeftChannels.clear();
    this.defaultRightChannels.clear();
    if (webProxyAudioDownMixInfo) {
      webProxyAudioDownMixInfo.downMixChannelSets[0].forEach(ch => this.defaultLeftChannels.add(ch));
      webProxyAudioDownMixInfo.downMixChannelSets[1].forEach(ch => this.defaultRightChannels.add(ch));
    }
  }


  private getAudioManager(): AudioManager | null {
    return this.audioManagerState.audioManager;
  }

  public  getChannelsAsMatrix(): number[][] | null {
    return this.getAudioManager()?.getChannelsAsMatrix() ?? null;
  }

  public goForwardBySecond() {
    this.goForward(1000);
  }

  public goForwardByTenSeconds() {
    this.goForward(10000);
  }

  public goBackwardBySecond() {
    this.goBackward(1000);
  }

  public goBackwardByTenSeconds() {
    this.goBackward(10000);
  }

  public goForward(ms: number) {
    this.seekToClampedPlayerPts(this.getNominalPts().approximate + ms / 1000);
  }

  public goBackward(ms: number) {
    this.seekToClampedPlayerPts(this.getNominalPts().approximate - ms / 1000);
  }

  /**
   * This is a nominal value, meaning that it reflects the position we want the
   * player to be in, not the position which the player actually is in. The
   * nominal and actual values differ during seeking.
   */
  private static calculateNominalPts(currentTime: number | null, nextCurrentTime: number | null): FuzzyValue {
    return { approximate: nextCurrentTime ?? currentTime ?? 0 };
  }

  private getNominalPts(): FuzzyValue {
    return ItemMediaVideoPlayer.calculateNominalPts(
      this.currentTime.value,
      this.nextCurrentTime.value,
    );
  }

  public updateAudioTrackConfig(update: AudioTrackConfigUpdate[]): void {
    const audioManager = this.getAudioManager();
    if (audioManager === null) {
      this.streamingPlaybackState?.updateAudioTrackConfig(update);
      return;
    }
    audioManager.updateAudioTrackConfig(update);

    if (!this.paused.value) {
      audioManager.schedulePlayout();
    }
  }

  private setSrcCore(src: string) {
    if (!this.player.paused) {
      /**
       * We manually update our proxy paused state, because due to setting a new src
       * the paused event can get swallowed and not fire, resulting in our proxy state being out of sync.
       */
      this.paused.value = true;
    }

    // clear the last error before attempting a new src
    this.lastError.value = null;
    this.hasMedia.value = false;
    this.player.src = src;

    this.hasGrowingMedia.value = (this.streamingPlaybackState !== null) && (this.streamingPlaybackState.getMediaUrl() === src);
  }


  public setItemId(
    itemId: string,
    _itemType: 'video' | 'audio',
    flags: {
      useNewBufferingLogic: boolean,
    },
    callbacks?: {
      noPlayableArtifactYet?: () => void,
      postLoadOperation?: PostLoadOperation,
    },
  ) {
    if (this.itemId === itemId) {
      return;
    }

    // We are loading a new item
    this.peakCount.value = 0;
    this.itemId = itemId;
    this.clear(false);
    this.failedQualities.clear();

    let startPositionHintInMs: number | undefined;

    const postLoadOperation = callbacks?.postLoadOperation;

    if (postLoadOperation?.type === 'callback') {
      this.addPostLoadOperation(postLoadOperation.callback);
    } else if (postLoadOperation?.type === 'seek') {
      startPositionHintInMs = postLoadOperation.positionInMs;
      if (postLoadOperation.play) {
        this.addPostLoadOperation(() => this.play(postLoadOperation.positionInMs));
      } else {
        this.addPostLoadOperation(() => this.seekToVirtualClipMediaPosition({ approximate: postLoadOperation.positionInMs / 1000.0 }));
      }
    }
    this.loadItem(itemId, flags, startPositionHintInMs, callbacks?.noPlayableArtifactYet).catch(err =>  console.error('Error in loadItem()', err));
  }


  /**
   * Returns the item or null if the item does not exist
   */
  private async fetchItem(apiActions: VideoApiActions, itemId: string): Promise<PlayableItemDto | null> {
    try {
      const options: PlayableItemGetOptions = {
        ...this.selectArtifactOptions,
      };
      const freshItem = await apiActions.getPlayableItem(itemId, options);
      return freshItem;
    } catch (err) {
      console.error(`Could not fetch item ${itemId}`);
      return null;
    }
  }

  private completeSeek() {
    this.seekIfApplicable();
    this.resetSeekingState();
    this.timeUpdate();
  }

  private resetSeekingState() {
    this.seeking.value = false;
    if (this.seekFailSafe !== null) {
      clearTimeout(this.seekFailSafe);
      this.seekFailSafe = null;
    }
  }

  private async loadItem(
    itemId: string,
    flags: {
      useNewBufferingLogic: boolean,
    },
    startPositionHintInMs: number | undefined,
    noPlayableArtifactYet: (() => void) | undefined,
  ) {
    while (true) {
      this.item = await this.fetchItem(this.services.apiActions, itemId);
      if (this.item === null) {
        return;
      }

      if  (this.item.playableArtifact !== null) {
        break;
      }

      const itemType = this.item.itemType;
      if (itemType !== ItemType.VIDEO && itemType !== ItemType.AUDIO && itemType !== ItemType.IMAGE) {
        // Other item types are not playable
        return;
      }

      if (noPlayableArtifactYet) {
        noPlayableArtifactYet();
      }

      const timeoutMs = getPollDelayForItem(this.item);
      console.log(`Item ${itemId} is not yet playable. Re-trying in ${timeoutMs} ms`);
      await asyncTimeout(timeoutMs);

      if (this.itemId !== itemId) {
        // If the item has been cleared while we were waiting, let's quit
        return;
      }
    }

    let playableArtifact: PlayableArtifact | null = null;

    // Get the default playable artifact - use first playable from availableArtifacts if present,
    // otherwise fall back to playableArtifact for backward compatibility
    if (this.item.availableArtifacts) {
      const firstPlayable = this.item.availableArtifacts.find((a) => a.playableStatus === 'playable');
      if (firstPlayable) {
        const { playableStatus, ...artifact } = firstPlayable;
        playableArtifact = artifact;
      }
    } else {
      playableArtifact = this.item.playableArtifact;
    }

    // Check if there is last selected quality that's available and playable
    // and switch to that if needed
    const userLastSelectedQuality = getLastSelectedQuality();
    if (userLastSelectedQuality) {
      const availableQualities = getAvailableQualitiesFromItem(this.item);

      const itemDefaultQuality = detectQualityFromArtifact(playableArtifact);
      const qualityToUse = selectQualityForLoad(availableQualities, itemDefaultQuality, userLastSelectedQuality);

      if (qualityToUse && qualityToUse !== itemDefaultQuality) {
        const selectedArtifact = this.selectArtifactByQuality(qualityToUse);
        if (selectedArtifact) {
          playableArtifact = selectedArtifact;
        }
      }
    }

    if (playableArtifact === null) {
      console.warn(`Item ${itemId} has no playable artifacts available`);
      return;
    }

    // Now that we have a playable artifact, we can initialize the player
    this.updateDefaultLeftAndRightChannels(this.item.webProxyAudioDownMixInfo);
    this.languageCode = this.item.languageCode ?? 'en';
    this.setOffsetFromVirtualClipmodel(this.item.virtualClipInfo);

    const browser = getBehaviorFromBrowserEnvironment();
    this.framePtsConversion.value = getFramePtsConversion(this.item, playableArtifact, browser);
    this.currentArtifact = playableArtifact;
    if ((playableArtifact.type === 'stream') || (playableArtifact.type === 'tams')) {
      this.initStreamedPlayback(this.item, playableArtifact, startPositionHintInMs, flags);
    } else {
      this.clearStreamedPlayback();
      this.setSrc(playableArtifact.url);
    }

    if (this.item.itemType === ItemType.AUDIO) {
      this.setPosterImage(this.item.thumbnailUrl ?? '');
    } else {
      this.setPosterImage('');
    }
  }

  clearStreamedPlayback() {
    if (this.streamingPlaybackState) {
      this.streamingPlaybackState.cleanables.forEach((cleanable) => cleanable.cleanup());
      this.streamingPlaybackState = null;
    }
    this.isLive = false;
  }

  private getDefaultAudioTrackConfig(trackIndex: number): AudioTrackConfig {
    const isEnabled =
      (this.defaultLeftChannels.has(trackIndex)) ||
      (this.defaultRightChannels.has(trackIndex)) ||
      (this.defaultRightChannels.size + this.defaultLeftChannels.size === 0);

    return { isMuted: !isEnabled, isSolo: false };
  }

  private initStreamedPlayback(
    item: PlayableItemDto,
    artifact: PlayableArtifactStream | PlayableArtifactTams,
    firstSegmentTimeInMsHint: number | undefined,
    flags: {
      useNewBufferingLogic: boolean,
    },
  ) {
    const setPeaksManager = (peaksManager: PeaksManagerOps) => this.peaksManager = peaksManager;
    loadPeaks(item.id, this.services.itemPollingOps, id => this.pollListenerIds.push(id), setPeaksManager, null, this.peakCount, new NoOpM4aProxiesCallback());

    this.clearStreamedPlayback();

    // The point with this whole code block is to try to avoid loading segment 0, 1, 2 etc when seeking
    // directly into the middle of the item (performance being the motivation). In order to avoid this
    // we need to seek the player to the start time BEFORE loading the MediaSourceExtension so that we
    // don't accidentally start fetching the first segments.
    //
    // In order to do this we need to seek the player, but we cannot go through the regular seek
    // functions like seekToVirtualClipMediaPosition or seekToSourceMediaPosition for two reasons:
    //
    //   1. If we call this before we have completely loaded the item (which is the state we are
    //      in now) these calls will be deferred till after the item is load, but then it is too
    //      late because then the MSE has already requested segment 0, 1, 2 etc.
    //   2. These seek functions have sanity checks which clamps the seeks to within the duration
    //      of the currently loaded item. Since we're not done loading this item yet, the player
    //      has no duration and therefore we would be clamped to 0.
    //
    //Because of this we need to just manually calculate the player pts and directly force a seek.
    if ((firstSegmentTimeInMsHint !== undefined) && (this.framePtsConversion.value !== null)) {
      const sourceMediaPosition =
        ItemMediaVideoPlayer.virtualClipMediaPositionToSourceMediaPosition({ approximate: firstSegmentTimeInMsHint / 1000.0 }, this.startOffsetInMs.value);

      const pts = sourceMediaPositionToPts(sourceMediaPosition, this.framePtsConversion.value.frameDuration, this.framePtsConversion.value.delay);
      this.nextCurrentTime.value = pts.approximate;
      this.seekIfApplicable();
    }

    let sourceItemId = item.id;
    if (item.virtualClipInfo) {
      sourceItemId = item.virtualClipInfo.masterClipId;
    }

    // Determine streaming configuration based on artifact type and media properties
    const isLiveStream = artifact.type === 'stream' && (item.media?.streaming ?? false);

    // The safe segment block size is the number of pre-signed segment URLs
    // we can ask for in one urlArray lambda request without risking
    // hitting the AWS API Gateway payload limit.
    const safeSegmentBlockSize = 4000;

    // Determine how many segment URLs we need to be able
    // to background fetch ahead to achieve smooth scrubbing,
    // depending on whether this is a live stream or not.
    // Also ensuring that we do not exceed the safe segment block size.
    const totalSegmentCount = !isLiveStream && item.media?.segmentCount !== undefined
      ? Math.min(item.media.segmentCount, safeSegmentBlockSize)
      : safeSegmentBlockSize;

    const segmentResolver = new OptimizedSegmentUrlCache(
      this.services.apiActions,
      'get',
      10,
      totalSegmentCount,
    );
    invariant(this.streamingPlaybackState === null, 'There should be no streaming playback at this point');

    const itemIsLive = new ObservableState(false);
    const cleanables : Cleanable[] = [];

    const abortController = new AbortController();
    cleanables.push({ cleanup() { abortController.abort(); } });
    cleanables.push({ cleanup() { segmentResolver.close(); } });

    // Proxy the itemIsLive changes to the playerIsLive observable. This will be
    // cleaned up when the streaming playback state is cleaned up.
    cleanables.push(itemIsLive.observe((isLive) => this.isLive = isLive));

    let newStreamingPlaybackState : StreamingPlaybackState;
    if (flags.useNewBufferingLogic) {
      const logger = new ConsoleLogger();
      if (mimirDevEnabled()) {
        logger.setDebugMode(true);
      }

      const audioChannelCount = new ObservableState<number | null>(0);

      // If we use FLAC downmix we need to have a state for the full audio
      // track configuration. This we be null if we are not in FLAC mode.
      let audioTrackConfig : ObservableState<AudioTrackConfig[]> | null = null;

      // This is the mix matrix that will be passed in to the actual FLAC
      // downmix code.
      const mixMatrix = new ObservableState<number[][]>([]);

      const tsAudioController: TsAudioController = {
        kind: 'flacDownmix',
        matrix: mixMatrix,
      };

      // The truncated audio track config contains all the audio track configs
      // that we know will be valid. In other words the number of audio track
      // configs correlate with the number of audio tracks we know that we have
      // in the item being played. This will start of as an empty list until we
      // have determined how many audio tracks we actually have.
      const truncatedAudioTrackConfig = new ObservableState<AudioTrackConfig[]>([]);

      // When the number of audio channels changes we need to populate the
      // truncated audio track configs with the default configurations so
      // that the tracks will be visible in the UI.
      cleanables.push(audioChannelCount.observe((audioChannelCount) => {
        if (audioChannelCount === null) return;

        const configs = cloneDeep(truncatedAudioTrackConfig.value);
        // Make sure that the UI will present all the known tracks and
        // initialize them to default config
        while (configs.length < audioChannelCount) {
          configs.push(this.getDefaultAudioTrackConfig(configs.length));
        }
        // Make sure we don't present more audio track configs than there
        // are in the actual media
        truncatedAudioTrackConfig.value = configs.slice(0, audioChannelCount);
      }));

      cleanables.push(truncatedAudioTrackConfig.observe((newConfig) => {
        // We pass the currently known audio track configs directly to
        // the UI.
        this.audioTrackConfigs.value = newConfig;

        // We also need to calculate a mix matrix which we pass to the
        // FLAC downmix logic. Since this MUST have enough rows
        // to cover all the potential audio tracks and we don't know how
        // many tracks until after the first downmix operation we just
        // need to make sure that this matrix is large enough to support
        // the maximum number of channels we support.
        const fullAudioTrackConfigs = cloneDeep(newConfig);
        const maxNumberOfSupportedChannels = 16;
        while (fullAudioTrackConfigs.length < maxNumberOfSupportedChannels) {
          fullAudioTrackConfigs.push(this.getDefaultAudioTrackConfig(fullAudioTrackConfigs.length));
        }
        mixMatrix.value = createMixMatrixFromAudioTrackConfig(fullAudioTrackConfigs);
      }));

      audioTrackConfig = truncatedAudioTrackConfig;

      const cmafSegmentSources : CmafSegmentSource[] = [];

      if (artifact.type === 'tams') {
        const flowIds = artifact.flowIds;

        const tamsClient =
          createDefaultTamsClient(
            { fetch: this.services.fetch, logger },
            () => this.services.apiActions.getTamsToken(artifact.storageId),
          );

        for (const flowId of flowIds) {
          const tamsCmafSource =
            createTamsCmafSegmentSource(
              {
                fetch: this.services.fetch,
                asyncTransmuxerProvider: (abortSignal: AbortSignal) => {
                  return createWebWorkerAsyncTransmuxer(abortSignal, logger);
                },
                logger,
                tamsClient,
              },
              abortController.signal,
              this.id,
              flowId,
            );

          cmafSegmentSources.push(tamsCmafSource);
        }
      } else {
        invariant(item.media != null, 'Expected streaming media');

        const mimirCmafSources =
          createCmafSegmentSourcesForItem(
            {
              fetch: this.services.fetch,
              asyncTransmuxerProvider: (abortSignal: AbortSignal) => {
                return createWebWorkerAsyncTransmuxer(abortSignal, logger);
              },
              logger,
              urlResolver: segmentResolver,
              inspectTs: inspect,
              itemPollingOps: this.services.itemPollingOps,
              addPollListenerId: (id: string) => this.pollListenerIds.push(id),
            },
            this.id,
            item.id,
            item.media,
            item.playableDuration ?? FRAC_ZERO,
            artifact.streamSet,
            abortController.signal,
            tsAudioController,
            audioChannelCount,
          );
        cmafSegmentSources.push(...mimirCmafSources);
      }

      const mediaSourceProviderOps : MediaSourceProviderOps = {
        createMediaSource(): MediaSourceOps {
          const mediaSource = new MediaSource();
          return {
            onSourceClose(callback: () => void): void {
              mediaSource.onsourceclose = callback;
            },
            onSourceOpen(callback: () => void): void {
              mediaSource.onsourceopen = callback;
            },
            addSourceBuffer(codecString: string): SourceBufferOps {
              return mediaSource.addSourceBuffer(codecString);
            },
            url(): string {
              return URL.createObjectURL(mediaSource);
            },
            setLiveSeekableRange(start: number, end: number): void {
              mediaSource.setLiveSeekableRange(start, end);
            },
            set duration(value: number) {
              mediaSource.duration = value;
            }
          };
        }
      };

      const url =
        startMseBufferingAndReturnUrl(
          logger,
          mediaSourceProviderOps,
          cmafSegmentSources,
          calculatedObservable((timeOrNull) => timeOrNull ?? 0, cleanables, this.currentTime),
          abortController.signal,
          {
            allowedForwardSeekBuffering: fractionToFuzzy(item.media?.segmentDuration ?? { numerator: 2, denominator: 1 }),
            rewindBufferBalance: FRAC_HALF,
            earlyCacheEvictionTolerance: integerToFuzzy(2),
            bufferRangeSafetyMargin: integerToFuzzy(1),
            maxBufferedDurationSoftLimit: this.maxBufferedDuration,
          }
        );

      newStreamingPlaybackState = {
        cleanables,
        updateAudioTrackConfig(updates: AudioTrackConfigUpdate[]) {
          if (audioTrackConfig) {
            audioTrackConfig.value = updateAudioTrackConfig(audioTrackConfig.value, updates);
          }
        },
        getMediaUrl(): string {
          return url;
        },
        bufferMore(): void {
          // This system knows how to buffer more automatically.
        }
      };
    } else {
      if (artifact.type === 'tams') {
        throw new Error('TAMS playback only supported with the new buffering logic');
      }
      invariant(item.media != null, 'Expected streaming media');
      const activeBuffering =
        new ActiveBuffering(
          {
            id: sourceItemId,
            media: item.media,
            streamSet: artifact.streamSet,
            segmentResolver,
            playableDuration: item.playableDuration ?? FRAC_ZERO,
          },
          this.player,
          this.bufferingBlocker,
          this.bufferingTracking,
          this.bufferingDebug,
          this.audioTrackConfigs,
          this.id,
          this.defaultLeftChannels,
          this.defaultRightChannels,
        );

      cleanables.push({ cleanup() { activeBuffering.close(); } });

      newStreamingPlaybackState = {
        cleanables,
        updateAudioTrackConfig(update: AudioTrackConfigUpdate[]) {
          activeBuffering.updateAudioTrackConfig(update);
        },
        getMediaUrl(): string {
          return activeBuffering.getMediaUrl();
        },
        bufferMore(): void {
          activeBuffering.startBufferMore('itemMediaPlayer');
        }
      };

      itemIsLive.value = item.media?.streaming ?? false;

      const pollListenerId = this.services.itemPollingOps.addPollListener(item.id, ['streaming', 'playableDuration', 'mediaAvailability'], response => {
        if (response === null) {
          return;
        }

        itemIsLive.value = response.streaming;

        if (response.mediaAvailability && response.playableDuration !== null) {
          activeBuffering.updateMediaAvailabilityAndDuration(response.mediaAvailability, response.playableDuration);
        }
      });
      this.pollListenerIds.push(pollListenerId);

      if (this.startOffsetInMs.value > 0) {
        this.nextCurrentTime.value = ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(this.startOffsetInMs.value);
      }
    }

    this.setSrc(newStreamingPlaybackState.getMediaUrl());
    this.streamingPlaybackState = newStreamingPlaybackState;
  }

  private onPlaybackChange() {
    this.streamingPlaybackState?.bufferMore();
  }

  public setVtt(vttUrl: string | null) {
    this.vttUrl = vttUrl;
    this.setupVttTrack();
  }

  private setSrc(src: string) {
    this.cleanupAudioManager();
    this.hasAudioChannels.value = false;

    this.setSrcCore(src);
  }

  public setPosterImage(src: string) {
    // We are changing the url by adding an ampersand to it in order force a different URL than
    // what <v-img> tags in the search page have already fetched for the thumbnail. Otherwise, we run
    // into a very strange CORS error in Chrome, and the poster frame is not set. The issue happens when
    // <video> tries to fetch the poster frame, having crossorigin="anonymous" set on it, but <v-img> has
    // already fetched and cached the same URL differently (since <v-img> does not obey crossorigin
    // attribute like <img> does, and renders in a <div> ultimately). Similar issues:
    // https://support.google.com/chrome/thread/76617220?hl=en
    // https://bugs.chromium.org/p/chromium/issues/detail?id=409090
    let posterUrl = '';
    if (src) {
      posterUrl = avoidCORSConflict(src);
    }

    this.player.poster = posterUrl;
  }

  public appendVideoElementToElement(parent: Element): void {
    parent.appendChild(this.player);
    this.visible.value = true;
  }

  private getVolume() {
    return this.player.volume || 1; // 1.0 is the maximum volume
  }

  public setVolume(volume: number) {
    invariant(typeof volume === 'number', 'Video set volume argument must be a number');

    this.player.volume = volume;
    const audioManager = this.getAudioManager();
    if (this.hasAudioChannels && audioManager !== null) {
      audioManager.setVolume(volume);
    }
  }

  public setMuted(val: boolean) {
    this.explicitMutedState = val;
    const audioManager = this.getAudioManager();
    if (this.hasAudioChannels && audioManager !== null) {
      audioManager.setVolume(val ? 0 : this.getVolume());
    } else {
      this.player.muted = val;
    }
  }


  private speedFactor = new ObservableState<number>(0);
  private backwardsPlayingLastPosInMs = 0;
  private backwardsPlayingLastPosUpdate = 0;
  private backwardsPlayingInterval = -1;

  private onBackwardsPlayingTick() {
    const currentSpeedFactor = this.speedFactor.value;
    invariant(currentSpeedFactor < 0, `onTick() should not be invoked when speed factor is >= 0 (${this.speedFactor})`);

    // Let's calculate where we need to seek
    const now = performance.now();
    const elapsed = now - this.backwardsPlayingLastPosUpdate;
    const posDeltaInMs = elapsed * currentSpeedFactor;
    const newPosInMs = this.backwardsPlayingLastPosInMs + posDeltaInMs;

    if (newPosInMs < 0) {
      // If we have reached the beginning of the media, let's pause at 0
      this.pauseAndReset();
      this.seekToVirtualClipMediaPosition(integerToFuzzy(0));
      return;
    }

    if (newPosInMs >= this.durationInMs.value) {
      // If we have reached the end of the media, let's pause there
      this.pauseAndReset();
      this.seekToVirtualClipMediaPosition({ approximate: this.durationInMs.value / 1000.0 });
      return;
    }

    this.seekToVirtualClipMediaPosition({ approximate: newPosInMs / 1000.0 });
  }


  private pauseAndReset() {
    this.pause();
    this.noteCurrentPosition();
    this.pausedChange();
  }

  private noteCurrentPosition() {
    this.backwardsPlayingLastPosInMs = this.currentTimeInMs.currentValue();
    this.backwardsPlayingLastPosUpdate = performance.now();
  }


  public getSpeedFactorObs(): Observable<number> {
    return this.speedFactor.getObservable();
  }

  private stopBackwardsPlayingLoop() {
    if (this.backwardsPlayingInterval !== -1) {
      clearInterval(this.backwardsPlayingInterval);
      this.backwardsPlayingInterval = -1;
    }
  }

  private startBackwardsPlayingLoop() {
    this.noteCurrentPosition();
    this.stopBackwardsPlayingLoop();
    // setInterval returns a NodeJS.Timeout type that does not match runtime, in runtime this is a number, which also
    // happens to be expected by clearInterval() function, hence the as unknown as number cast.
    this.backwardsPlayingInterval = setInterval(() => this.onBackwardsPlayingTick(), 100) as unknown as number;
  }


  /**
   * The speedFactor value given to this function represents
   * the desired speed factor. Since playing backwards is not
   * universally supported, we simulate it by pausing the player
   * and seeking at regular intervals.
   */
  public setSpeedFactor(speedFactor: number) {
    this.speedFactor.value = speedFactor;

    if (speedFactor > 0) {
      this.player.playbackRate = speedFactor;
      this.playInternal();
    } else {
      this.pauseInternal();
    }

    if (speedFactor < 0) {
      this.startBackwardsPlayingLoop();
    } else {
      this.stopBackwardsPlayingLoop();
    }

    const audioManager = this.getAudioManager();
    if (this.hasAudioChannels && audioManager !== null) {
      audioManager.setPlaybackRate(speedFactor);
    }
  }

  /**
   * Start playing the video from current position, or from the time code given.
   * @param ms Optional - from which timestamp in milliseconds where to start playing
   */
  public play(ms?: number) {
    // Schedule playout has to be called before the video player is scheduled
    const audioManager = this.getAudioManager();
    if (audioManager !== null) {
      audioManager.schedulePlayout();
    }

    if (ms)
      this.seekToVirtualClipMediaPosition({ approximate: ms / 1000.0 });

    if (this.hasOffsetStartOrEnd() && this.getRoughVirtualMediaPosition() >= this.durationInMs.value) {
      this.seekToVirtualClipMediaPosition(integerToFuzzy(0));
    }

    this.setSpeedFactor(1);
  }

  private playInternal() {
    const playResult: (Promise<void> | undefined) = this.player.play();

    /*
      Modern browsers return a promise to report asynchronous errors, older
      browsers don't.

      It would be tempting to call the this.playerErrorCallbacks handlers when
      an error occurs here, but we can't since the playerErrorCallbacks
      handler expect PlayerError, while the play() call provides DOMException
      errors.
    */
    playResult?.catch(err => console.error(err));
  }

  public pause() {
    this.setSpeedFactor(0);
  }

  private pauseInternal() {
    const audioManager = this.getAudioManager();
    if (audioManager !== null)
      audioManager.stopAll();
    // Safeguard for DOM exception for multiple pause event
    if (!this.player.paused) {
      this.player.pause();

      // #5771 & #5573 - When pausing the player, it sometimes shows the wrong frame. Seeking seems more accurate,
      // By seeking one frame forward on each pause, the player is seemingly more frame accurate.
      this.goFrameForward(true);
    }
  }

  public togglePlayPause() {
    // We need to test the speed factor being non zero rather than this.paused
    // because if we are playing backwards, the video player is paused
    if (this.speedFactor.value !== 0) {
      this.pause();
    } else {
      this.play();
    }
  }


  public goFrameForward(avoidPausing = false) {
    this.seekToRelativeClampedFrame(1, avoidPausing);
  }

  public goFrameBackward() {
    this.seekToRelativeClampedFrame(-1, false);
  }

  private static calculateSourceMediaPosition(
    currentTime: number | null,
    nextCurrentTime: number | null,
    framePtsConversion: FramePtsConversion | null
  ): FuzzyValue | null {
    if (framePtsConversion == null) {
      return null;
    }

    /*
      When converting the nominal player PTS to a source media position, first
      snap it to a video frame.
    */
    const pts = ItemMediaVideoPlayer.calculateNominalPts(currentTime, nextCurrentTime);
    const frameTime = ptsToFrameTime(pts, framePtsConversion);
    return frameTimeToSourceMediaPosition(frameTime);
  }

  private static calculateVirtualClipMediaPosition(
    sourceMediaPosition: FuzzyValue | null,
    startOffsetInMs: number,
  ): FuzzyValue {
    const sourceMediaPositionOrZero = sourceMediaPosition ?? integerToFuzzy(0);
    if (startOffsetInMs !== 0) {
      return subtractFuzzy(sourceMediaPositionOrZero, { approximate: startOffsetInMs / 1000.0 });
    } else {
      return sourceMediaPositionOrZero;
    }
  }

  public getSourceMediaPositionObs(): Observable<FuzzyValue | null> {
    return this.sourceMediaPosition;
  }

  public getVirtualClipDurationObs(): Observable<FuzzyValue> {
    return this.virtualClipDuration;
  }

  public getSampleDuration(): FuzzyValue | null {
    if (this.item == null) {
      return null;
    }

    return this.item.frameDuration;
  }

  private seekToRelativeClampedFrame(frameDelta: number, avoidPausing: boolean) {
    if (this.framePtsConversion.value != null) {
      if (!this.paused.value && !avoidPausing) {
        this.togglePlayPause();
      }

      /*
        We use the nominal presentation timestamp (PTS) rather than the current
        PTS, since we want previous frame navigation to still take effect even
        though the seek is still pending.
      */
      const pts = this.getNominalPts();
      const frameTime = ptsToFrameTime(pts, this.framePtsConversion.value);
      frameTime.frameIndex += frameDelta;

      const newPts = frameTimeToPts(frameTime, this.framePtsConversion.value.delay);
      this.seekToClampedPlayerPts(newPts.approximate);
    }
  }

  public seekToClampedSourceMediaPosition(sourcePos: FuzzyValue) {
    if (this.framePtsConversion.value == null) {
      return;
    }
    const pts = sourceMediaPositionToPts(sourcePos, this.framePtsConversion.value.frameDuration, this.framePtsConversion.value.delay);
    this.seekToClampedPlayerPts(pts.approximate);
  }

  public seekToVirtualClipMediaPosition(virtualClipMediaPosition: FuzzyValue): void {
    const setTimeOperation = () => {
      this.seekToClampedSourceMediaPosition(
        ItemMediaVideoPlayer.virtualClipMediaPositionToSourceMediaPosition(virtualClipMediaPosition, this.startOffsetInMs.value),
      );
    };

    this.addPostLoadOperation(setTimeOperation);

  }


  private addPostLoadOperation(op: () => void) {
    if (this.hasMedia.value) {
      // If the media has already been loaded, let's execute the operation now
      op();
    } else {
      // Otherwise, let's schedule it for when it is ready
      this.postLoadOperations.push(op);
    }
  }


  private static virtualClipMediaPositionToSourceMediaPosition(virtualClipMediaPosition: FuzzyValue, startOffsetInMs: number) {
    if (startOffsetInMs === 0) {
      return virtualClipMediaPosition;
    } else {
      return addFuzzy(virtualClipMediaPosition, { approximate: startOffsetInMs / 1000.0 });
    }
  }

  private static calculateFirstVirtualFrameTime(
    startOffsetInMs: number,
    framePtsConversion: FramePtsConversion | null,
  ): FrameTime | null {
    if (framePtsConversion === null) {
      return null;
    }
    if (startOffsetInMs > 0) {
      /*
        TODO: Historically startOffsetInMs has been considered a PTS value,
        maybe we should reinterpet it as a media source time value instead?
      */
      const offsetPlayerPts = ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(startOffsetInMs);
      return ptsToFrameTime({ approximate: offsetPlayerPts }, framePtsConversion);
    }

    return {
      frameIndex: 0,
      frameDuration: framePtsConversion.frameDuration,
      rounded: false
    };
  }

  static calculateVirtualClipStart(
    startOffsetInMs: number,
    framePtsConversion: FramePtsConversion | null
  ): FuzzyValue | null {
    const firstFrame =
      ItemMediaVideoPlayer.calculateFirstVirtualFrameTime(startOffsetInMs, framePtsConversion);

    return (firstFrame == null) ? null : frameTimeToSourceMediaPosition(firstFrame);
  }

  public getVirtualClipStartObs(): Observable<FuzzyValue | null> {
    return this.virtualClipStart;
  }


  public getVirtualClipSourceMediaRangeObs(): Observable<MediaSelection> {
    return this.virtualClipSourceMediaRange;
  }

  private static calculateVirtualClipSourceMediaRange(
    startOffsetInMs: number,
    endOffsetInMs: number,
    virtualClipStart: FuzzyValue | null,
    framePtsConversion: FramePtsConversion | null,
    playerDuration: number,
  ): MediaSelection {
    return {
      whole: startOffsetInMs == 0 && endOffsetInMs == 0,
      bounds: {
        start: virtualClipStart ?? integerToFuzzy(0),
        end: ItemMediaVideoPlayer.calculateLastVirtualFrameSampleEnd(endOffsetInMs, framePtsConversion, playerDuration) ?? integerToFuzzy(0),
      }
    };
  }

  private static calculateLastVirtualFrameSampleEnd(
    endOffsetInMs: number,
    framePtsConversion: FramePtsConversion | null,
    playerDuration: number,
  ): FuzzyValue | null {
    if (framePtsConversion === null) {
      return null;
    }

    const maxPts = ItemMediaVideoPlayer.calculateMaxPts(endOffsetInMs, playerDuration);
    const frameTime = ptsToFrameTime({ approximate: maxPts }, framePtsConversion);
    return frameTimeToSourceMediaPosition(frameTime);
  }


  private seekToClampedPlayerPts(unclampledPlayerPts: number) {
    if (this.framePtsConversion.value == null) {
      return;
    }

    let minPts;
    if (this.startOffsetInMs.value > 0) {
      minPts = ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(this.startOffsetInMs.value);
    } else {
      const firstPresentableFrame: FrameTime = {
        frameIndex: this.framePtsConversion.value.minimumFrameIndex ?? 0,
        frameDuration: this.framePtsConversion.value.frameDuration,
        rounded: false
      };
      minPts = frameTimeToPts(firstPresentableFrame, this.framePtsConversion.value.delay).approximate;
    }

    const seekMax = this.getMaxSeekPts();
    const clampedPts = Math.max(minPts, Math.min(unclampledPlayerPts, seekMax));

    this.nextCurrentTime.value = clampedPts;

    this.seekIfApplicable();
  }

  private static calculateMaxPts(
    endOffsetInMs: number,
    playerDuration: number,
  ) {
    if (endOffsetInMs > 0) {
      return ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(endOffsetInMs);
    }

    /*
      Setting video.currentTime to video.duration will not always seek to
      the last frame, but it is the best thing we can do for now.

      This is because, until the video has actually played to the end, the
      duration value it provides is based on the metadata of the media rather
      than how much media the decoder was able to decode.

      As an example, a 50 FPS, 10 second test clip encoded by Mxcoder will be
      reported as having a duration of 10.0 initially. However after having
      played the video to the end, duration will be given as 10.021333.

      This is most likely caused implicit decoder delay associated with the
      AAC packet used to prime the AAC audio decoder.

      At any given time, if we try to set a currentTime value that is greater
      than the reported duration, the Chrome video player will automatically
      clamp the value to the duration, and refuse to seek beyond it.

      This means that seeking to the actual last frame will only be possible
      after the video has been played to the end.
    */
    return isNaN(playerDuration) ? Infinity : playerDuration;
  }

  // Return reasonable value to clamp to in order to avoid interruptions while playing and also be able to seek the end of the video
  private getMaxSeekPts(): number {
    const segmentDuration: Fraction | undefined = this.item?.media?.segmentDuration;
    const seekEnd = ItemMediaVideoPlayer.calculateMaxPts(this.endOffsetInMs.value, this.player.duration);
    if (segmentDuration && this.isLive && this.speedFactor.value > 0) {
      return Math.max(seekEnd - approximateFractionValue(segmentDuration), 0);
    } else {
      return seekEnd;
    }
  }

  private seekIfApplicable() {
    // We want to wait until the current seek has finished before we seek again
    if (this.seeking.value) {
      return;
    }
    this.performPendingSeek();
  }

  private performPendingSeek() {
    const targetTime = this.nextCurrentTime.value;
    if (targetTime == null) {
      return;
    }

    this.nextCurrentTime.value = null;

    this.seeking.value = true;
    this.seekFailSafe = setTimeout(() => this.completeSeek(), 1000.0);
    this.player.currentTime = targetTime;

    this.timeUpdate();

    const audioManager = this.getAudioManager();
    if (!this.paused.value && audioManager !== null) {
      audioManager.schedulePlayout();
    }
  }

  private timeUpdate() {
    const _roughVirtualMediaPosition = ItemMediaVideoPlayer.playerPtsToRoughVirtualMediaPosition(this.player.currentTime, this.startOffsetInMs.value);
    if (this.hasOffsetStartOrEnd() && _roughVirtualMediaPosition >= this.durationInMs.value && !this.player.paused) {
      this.pause();
    }
    this.currentTime.value = this.player.currentTime;
  }

  /**
   * The returned value is only a rough approximation, since the implementation
   * assumes that the video player assigns the first video element a
   * presentation timestamp of zero, which is not always the case.
   */
  private static playerPtsToRoughVirtualMediaPosition(playerPts: number, startOffsetInMs: number) {
    return playerPts * 1000 - startOffsetInMs;
  }

  /**
   * Get a rough approximation of the nominal media position expressed in
   * milliseconds within the current virtual clip. I.e. the media distance from
   * the frame which which we consider the first frame in the (potentially
   * virtual) clip the player is currently presenting.
   *
   * This is only a rough approximation, since the implementation assumes that
   * the video player assigns the first video element a presentation timestamp of
   * zero, which is not always the case.
   */
  private static calculateRoughVirtualMediaPosition(currentTime: number | null, startOffsetInMs: number) {
    if (currentTime == null) {
      return 0;
    }
    return ItemMediaVideoPlayer.playerPtsToRoughVirtualMediaPosition(currentTime, startOffsetInMs);
  }

  private getRoughVirtualMediaPosition() {
    return ItemMediaVideoPlayer.calculateRoughVirtualMediaPosition(this.currentTime.value, this.startOffsetInMs.value);
  }


  private durationChange() {
    invariant(this.item, 'Expected item on durationChange');
    if (!isFinite(this.player.duration)) {
      console.warn('VideoPlayer element reported non-finite duration. Ignoring duration change event.');
      return;
    }

    const playerDuration = this.player.duration * 1000;
    const technicalDuration = this.item.durationFromTechnicalMetadata ?? 0;
    this.sourceMediaDuration.value = { approximate: this.player.duration };
    this.durationInMs.value = this.hasOffsetStartOrEnd() ? (this.endOffsetInMs.value - this.startOffsetInMs.value) : Math.max(playerDuration, technicalDuration);
    this.playerDuration.value = this.player.duration;
  }

  private clearVttTracks() {
    // We are potentially adding a new track, and we should disable all previous text tracks. There is no API for removing
    // tracks explicitly, though it is possible to remove the track from the DOM it will still linger. Research on this
    // topic seems to indicate that setting the mode to 'disabled' is the best way to go.
    const tracks = this.player.textTracks;
    for (const t of tracks) {
      t.mode = 'disabled';
    }
  }

  private setupVttTrack() {
    this.clearVttTracks();

    // Create a new track if current item has a vtt link.
    if (this.vttUrl !== null) {
      const track = document.createElement('track');
      track.kind = 'subtitles';
      track.label = this.languageCode;
      track.default = true;
      track.srclang = this.languageCode;
      track.src = this.vttUrl;
      this.player.appendChild(track);
      track.addEventListener('load', () => {
        // Apply virtual clip offset.
        this.checkForCaptionsOffset();
      });

      // Firefox requires manually setting the track to showing, chrome does not.
      // Instinctively, it feels like this should be in the track load handler - however, that seems to only
      // work the first time in firefox, resulting in only the first video showing subtitles.
      const updatedTracks = this.player.textTracks;

      updatedTracks[updatedTracks.length - 1].mode = 'showing';
    }
  }

  private setOffsetFromVirtualClipmodel(virtualClipInfo: VirtualClipInfo | null) {
    if (virtualClipInfo === null) {
      this.startOffsetInMs.value = 0;
      this.endOffsetInMs.value = 0;
    } else {
      this.startOffsetInMs.value = virtualClipInfo.inPoint;
      this.endOffsetInMs.value = virtualClipInfo.outPoint;
    }
  }

  private hasOffsetStartOrEnd() {
    return this.startOffsetInMs.value > 0 || this.endOffsetInMs.value > 0;
  }

  private pausedChange() {
    this.paused.value = this.player.paused && this.backwardsPlayingInterval === -1;
    if (this.paused.value) this.setSpeedFactor(0);
  }
  private addPlayerEventListener(event: HtmlVideoElementEventType, callback: (error?: any) => void) {
    const player = this.player;
    player.addEventListener(event, callback, false);
    this.cleanables.push({
      cleanup() {
        player.removeEventListener(event, callback);
      }
    });
  }

  private hookListeners() {
    this.addPlayerEventListener('resize', () => {
      this.videoAspectRatio.value = this.player.videoHeight === 0 ? null : (this.player.videoWidth / this.player.videoHeight);
      this.playerAspectRatio.value =  this.player.clientHeight === 0 ? null : (this.player.clientWidth / this.player.clientHeight);
    });
    this.addPlayerEventListener('play', () => {
      this.pausedChange();
    });
    this.addPlayerEventListener('pause', () => {
      this.pausedChange();
    });
    this.addPlayerEventListener('durationchange', () => {
      this.durationChange();
    });
    this.addPlayerEventListener('canplay', () => {
      this.seekIfApplicable();
    });
    this.addPlayerEventListener('timeupdate', () => {
      this.onPlaybackChange();
    });
    this.addPlayerEventListener('seeking', () => {
      this.onPlaybackChange();
    });
    this.addPlayerEventListener('seeked', () => {
      this.updateBuffered();
      this.completeSeek();
      if (this.imageSeekEvent) {
        this.imageSeekEvent.cb();
        this.cleanUpImageSeekEvent();
      }
    });
    this.addPlayerEventListener('loadedmetadata', () => {
      this.hasMedia.value = true;
      this.loading.value = false;
      for (const postLoadOperation of this.postLoadOperations.splice(0)) {
        postLoadOperation();
      }
      /**
       * If we ever load new videos, ensure proxy audio stays off if
       * we used audio channels.
       */
      if (this.hasAudioChannels.value) {
        this.player.muted = true;
      }

      this.setupVttTrack();
      this.onPlaybackChange();
      this.seekIfApplicable();
    });
    this.addPlayerEventListener('loadstart', () => {
      if (this.ignoreNextError) return;
      this.loading.value = true;
    });
    this.addPlayerEventListener('waiting', () => {
      this.playerWaiting = true;
    });
    this.addPlayerEventListener('playing', () => {
      this.playerWaiting = false;
      this.startAnimationLoop();
    });
    this.addPlayerEventListener('error', (err) => {
      // store the last error so we can use this information in order to attempt a
      // different version of the media (e.g., reset to proxy when setting highRes
      // fails for unreachable URL)
      this.lastError.value = err;

      // Player will report an error if we clear the video source, we want
      // to control ignoring it (otherwise the UI will show an error toast message)
      if (this.ignoreNextError) {
        this.ignoreNextError = false;
        return;
      }

      for (const callback of this.playerErrorCallbacks) {
        callback(err);
      }
      this.loading.value = false;
    });
    this.addPlayerEventListener('progress', () => { this.updateBuffered(); });
  }

  /**
   * In Virtual Clips we load a full proxy based on the source item, but the subtitles are properly cut
   * to the small segment the virtual clip represents.
   *
   * To ensure the subtitles stay in sync, we have to offset each cue by the start offset to ensure the
   * subtitles are in sync.
   *
   * This function should be called whenever a VTT link is set on the player.
   */
  private checkForCaptionsOffset() {
    if (this.hasOffsetStartOrEnd()) {
      Array.from(this.player.textTracks).forEach((track) => {
        if (track.cues) {
          Array.from(track.cues).forEach((cue) => {
            cue.startTime += ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(this.startOffsetInMs.value);
            cue.endTime += ItemMediaVideoPlayer.roughSourceMediaPositionToPlayerPts(this.startOffsetInMs.value);
          });
        }
      });
    }
  }

  private updateBuffered() {
    const timeRanges : TimeRange[] = [];
    for (let i = 0; i < this.player.buffered.length; i++) {
      timeRanges.push({
        start: this.player.buffered.start(i) * 1000.0,
        end: this.player.buffered.end(i) * 1000.0,
      });
    }
    if (JSON.stringify(timeRanges) != JSON.stringify(this.buffered.value)) {
      this.buffered.value = timeRanges;
    }
  }

  private static roughSourceMediaPositionToPlayerPts(roughVirtualMediaPosition: number) {
    return roughVirtualMediaPosition / 1000;
  }

  private imageSeekEvent: {
    player: HTMLVideoElement,
    cb: () => void,
    reject: () => void,
    called: boolean,
  } | null = null;

  private cleanUpImageSeekEvent() {
    if (this.imageSeekEvent && !this.imageSeekEvent.called) {
      this.imageSeekEvent.reject();
    }
    this.imageSeekEvent = null;
  }

  private seekAndGrabSemaphore = new PrioritySemaphore(1);

  public async seekAndGrabImage(sourcePos: FuzzyValue) {
    return await this.seekAndGrabSemaphore.limit(() => {
      return new Promise<string>((resolve, reject) => {
        this.imageSeekEvent = {
          player: this.player,
          cb() {
            this.called = true;
            const canvas = document.createElement('canvas');
            canvas.height = this.player.videoHeight;
            canvas.width = this.player.videoWidth;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(this.player, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL());
            }
            reject(new Error('Unable to obtain 2D canvas for drawing video frame'));
          },
          reject,
          called: false
        };

        this.seekToClampedSourceMediaPosition(sourcePos);
      });
    }, makePriority(), null);
  }

  /**
   * We use requestAnimationFrame instead of the timeupdate event because
   * it has a much better granularity without which the transcript cursor
   * movement is not smooth at all.
   */
  private startAnimationLoop() {
    if (!this.useAnimationLoop) {
      return;
    }

    if (this.animationFrameRequested) {
      return;
    }

    const onAnimationFrame = () => {
      this.animationFrameRequested = false;
      this.timeUpdate();

      const audioManager = this.getAudioManager();
      if (audioManager !== null && !this.player.paused) {
        if (this.player.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          if (this.playerWaiting && audioManager.hasScheduledSources()) {
            audioManager.stopAll();
          } else if (!this.playerWaiting) {
            if (!audioManager.hasScheduledSources()) {
              audioManager.schedulePlayout();
            }
            audioManager.playScheduled((this.currentTime.value ?? 0) * 1000.0);
          }
        }
      }
      if (this.isPlaybackProgressing()) {
        this.animationFrameRequested = true;
        requestAnimationFrame(onAnimationFrame);
      }
    };
    requestAnimationFrame(onAnimationFrame);
  }

  private isPlaybackProgressing() {
    return !(this.player == null || this.player.paused || this.player.ended || this.player.seeking || this.player.readyState < 3);
  }

  public addPlayerErrorCallback(callback: PlayerError) {
    this.playerErrorCallbacks.push(callback);
  }

  public removePlayerErrorCallback(callback: PlayerError) {
    const index = this.playerErrorCallbacks.indexOf(callback, 0);
    if (index > -1) {
      this.playerErrorCallbacks.splice(index, 1);
    }
  }

  /**
   * Clear the current video source.
   */
  public clear(clearItem = true) {
    if (clearItem) {
      this.itemId = null;
      this.item = null;
    }

    for (const id of this.pollListenerIds.splice(0)) {
      this.services.itemPollingOps.removePollListener(id);
    }

    this.peaksManager = null;

    this.postLoadOperations.splice(0);

    // The play/pause button depends on both paused and speed factor to
    // display itself correctly, so let's reset these values when clearing
    // the player
    this.paused.value = true;
    this.speedFactor.value = 0;
    // The player will throw an error when clearing it - we want to ignore it.
    this.clearStreamedPlayback();

    this.cleanupAudioManager();

    this.ignoreNextError = true;
    this.setPosterImage('');

    this.player.removeAttribute('src');
    this.player.load();
    this.player.muted = false;
    this.hasMedia.value = false;
    this.hasGrowingMedia.value = false;

    this.clearVttTracks();

    // Reset internal states
    this.durationInMs.value = 0;
    this.currentTime.value = null;
    this.startOffsetInMs.value = 0;
    this.endOffsetInMs.value = 0;
    this.nextCurrentTime.value = null;
    this.cleanUpImageSeekEvent();
    this.audioTrackConfigs.value = [];
  }

  private cleanupAudioManager() {
    if (this.audioManagerState.audioManager !== null) {
      this.audioManagerState.audioManager.cleanup();
      this.audioManagerState.audioManager = null;
      this.audioManagerState = { audioManager: null };
    }
  }

  private qualityChangeStateObs = new ObservableState<QualityChangeState>({
    inProgress: false,
    error: undefined,
  });

  /**
   * Track qualities that failed to load for the current item.
   */
  private failedQualities = new Map<PlaybackQuality, string>();

  /**
   * Find an artifact by quality from available artifacts.
   */
  private selectArtifactByQuality(quality: PlaybackQuality): PlayableArtifact | null {
    if (!this.item) {
      return null;
    }

    for (const artifact of this.item.availableArtifacts) {
      const artifactQuality = detectQualityFromArtifact(artifact);
      if (artifactQuality === quality) {
        return artifact;
      }
    }

    return null;
  }

  /**
   * Capture the current playback state for preservation during quality change.
   */
  private capturePlaybackState() {
    return {
      wasPlaying: !this.paused.value,
      currentTimeInMs: this.currentTimeInMs.currentValue(),
      volume: this.player.volume,
      muted: this.explicitMutedState,
      speedFactor: this.speedFactor.value,
    };
  }

  /**
   * Restore playback state after loading new artifact.
   */
  private async restorePlaybackState(state: ReturnType<typeof ItemMediaVideoPlayer.prototype.capturePlaybackState>) {
    // Wait for media to be ready before restoring state
    await this.waitForMediaReady();
    this.resetSeekingState();
    if (state.currentTimeInMs !== null) {
      this.seekToVirtualClipMediaPosition({ approximate: state.currentTimeInMs / 1000.0 });
    }

    this.setVolume(state.volume);
    this.setMuted(state.muted);
    this.setSpeedFactor(state.speedFactor);


    if (state.wasPlaying) {
      this.play();
    }
  }

  private async waitForMediaReady(): Promise<void> {
    if (this.hasMedia.value) {
      return;
    }

    return new Promise((resolve, reject) => {
      const cleanups: Cleanable[] = [];

      const cleanup = () => {
        cleanups.forEach(c => c.cleanup());
      };

      cleanups.push(this.hasMedia.observe((hasMedia) => {
        if (hasMedia) {
          cleanup();
          resolve();
        }
      }));

      cleanups.push(this.lastError.observe((error) => {
        if (error) {
          cleanup();
          const errorMessage = error instanceof Error ? error.message : 'Failed to load media';
          reject(new Error(errorMessage));
        }
      }));
    });
  }

  /**
   * Load a new artifact, handling both segmented and flat media.
   */
  private async loadNewArtifact(
    artifact: PlayableArtifact,
    startPositionHintInMs: number,
    flags: { useNewBufferingLogic: boolean }
  ): Promise<void> {
    if (!this.item) {
      throw new Error('Cannot load artifact: item or apiActions not available');
    }

    // Update frame PTS conversion for the new artifact
    const browser = getBehaviorFromBrowserEnvironment();
    this.framePtsConversion.value = getFramePtsConversion(this.item, artifact, browser);

    // Load based on artifact type
    if (artifact.type === 'stream' || artifact.type === 'tams') {
      this.initStreamedPlayback(this.item, artifact, startPositionHintInMs, flags);
    } else {
      this.clearStreamedPlayback();
      const hasExistingSeparateAudioProxies = this.hasAudioChannels.value && this.getAudioManager() !== null;
      // For flat-to-flat quality changes with separate audio proxies, keep the same audio manager and just update the source.
      if (hasExistingSeparateAudioProxies) {
        this.setSrcCore(artifact.url);
      } else {
        // For segmented-to-flat clear stale audioTrackConfigs
        // from the previous segmented playback so that loadAudio() can
        // set up a fresh AudioManager for the new flat artifact.
        this.audioTrackConfigs.value = [];
        this.setSrc(artifact.url);
      }
    }
    this.currentArtifact = artifact;
  }

  /**
   * Get the observable for quality change state.
   * Tracks loading progress and errors during quality changes.
   */
  public getQualityChangeStateObs(): Observable<QualityChangeState> {
    return this.qualityChangeStateObs.getObservable();
  }

  /**
   * Get available quality options for the current item.
   */
  public getAvailableQualities(): QualityOption[] {
    if (!this.item) {
      return [];
    }

    const qualities = getAvailableQualitiesFromItem(this.item);

    const result = qualities.map(option => {
      const failedError = this.failedQualities.get(option.quality);
      if (failedError) {
        return {
          ...option,
          disabledText: failedError,
        };
      }
      return option;
    });
    return result;
  }

  /**
   * Get the current playback quality.
   * Detects quality based on the currently loaded artifact.
   */
  public getCurrentQuality(): PlaybackQuality | null {
    return detectQualityFromArtifact(this.currentArtifact);
  }

  /**
   * Change the playback quality.
   * Captures current state, loads new artifact, and restores playback state.
   * Falls back to previous quality if loading fails.
   */
  public async changeQuality(selectedQuality: PlaybackQuality, flags: { useNewBufferingLogic: boolean }): Promise<QualitySelectionResult> {
    const previousQuality = this.getCurrentQuality();

    if (previousQuality === selectedQuality) {
      return {
        success: true,
        quality: selectedQuality,
      };
    }

    this.qualityChangeStateObs.value = { inProgress: true };
    const savedState = this.capturePlaybackState();

    try {
      const newArtifact = this.selectArtifactByQuality(selectedQuality);
      if (!newArtifact) {
        throw new Error(`Quality "${selectedQuality}" is not available for this item`);
      }
      await this.loadNewArtifact(newArtifact, savedState.currentTimeInMs, flags);
      await this.restorePlaybackState(savedState);

      this.qualityChangeStateObs.value = { inProgress: false };
      return {
        success: true,
        quality: selectedQuality,
      };
    } catch (error) {
      const errorMessage = error instanceof Error && error.message ? error.message : 'Failed to load media';
      this.qualityChangeStateObs.value = { inProgress: false, error: errorMessage };

      this.failedQualities.set(selectedQuality, errorMessage);

      if (previousQuality && previousQuality !== selectedQuality) {
        try {
          const fallbackArtifact = this.selectArtifactByQuality(previousQuality);
          if (fallbackArtifact) {
            await this.loadNewArtifact(fallbackArtifact, savedState.currentTimeInMs, flags);
            await this.restorePlaybackState(savedState);
            this.qualityChangeStateObs.value = { inProgress: false };
          }
        } catch (fallbackError) {
          console.error('Fallback to previous quality also failed:', fallbackError);
        }
      }

      return {
        success: false,
        quality: selectedQuality,
        error: errorMessage,
      };
    }
  }

  /**
   * Clean up the component and unload the HTML5 DOM element.
   */
  public destroy() {
    this.visible.value = false;

    this.cleanables.splice(0).forEach((cleanable) => cleanable.cleanup());

    this.player.remove();

    if (this.periodicBufferedUpdateTimeoutId !== null) {
      clearTimeout(this.periodicBufferedUpdateTimeoutId);
      this.periodicBufferedUpdateTimeoutId = null;
    }

    this.clear();
  }

  public getAudioTrackConfigsObs() {
    return this.audioTrackConfigs.getObservable();
  }

  public getPeakSeriesCount(trackIndex: number): number {
    const audioManager = this.getAudioManager();
    if (audioManager !== null) {
      return audioManager.getPeakSeriesCount(trackIndex);
    }

    if (this.peaksManager !== null) {
      return this.peaksManager.getPeakSeriesCount(trackIndex);
    }

    return 0;
  }

  public getPeaksForSourceMediaRange(trackIndex: number, width: number, channel: number, range: PeakRange): number[] {
    const audioManager = this.getAudioManager();
    if (audioManager !== null) {
      return audioManager.getPeaks(trackIndex, channel, range, width);
    }

    if (this.peaksManager !== null) {
      return this.peaksManager.getPeaks(trackIndex, channel, range, width);
    }

    return [];
  }

  public toggleFullscreen(): void {
    const player = this.player as any;
    if (player?.webkitEnterFullScreen) {
      if (document.fullscreenElement) {
        void document.exitFullscreen();
      } else {
        player.webkitEnterFullScreen();
      }
      return;
    }
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void player.requestFullscreen();
    }
  }

  public getHasGrowingMediaObs() {
    return this.hasGrowingMedia.getObservable();
  }

  public getLoadingObs() {
    return this.loading.getObservable();
  }

  public getSourceMediaDurationObs(): Observable<FuzzyValue> {
    return this.sourceMediaDuration.getObservable();
  }

  public getHasAudioChannelsObs() {
    return this.hasAudioChannels.getObservable();
  }

  public getVideoAspectRatioObs() {
    return this.videoAspectRatio.getObservable();
  }

  public getPlayerAspectRatioObs() {
    return this.playerAspectRatio.getObservable();
  }

  public getSeekingObs() {
    return this.seeking.getObservable();
  }

  public getCurrentFrameTimeObs() {
    return this.currentFrameTime;
  }

  public getVirtualClipMediaPositionObs() {
    return this.virtualClipMediaPosition;
  }

  public getLastErrorObs() {
    return this.lastError.getObservable();
  }

  public getBufferedObs(): Observable<TimeRange[]> {
    return this.buffered.getObservable();
  }

  public getPeakCountObs(): Observable<number> {
    return this.peakCount.getObservable();
  }

  public getCurrentSubtitleCueObs(): Observable<SubtitleCue | null> | null {
    return null;
  }

  private audioVisualizationState = new ObservableState<AudioVisualizationState | null>(null);
  public getAudioVisualizationStateObs(): Observable<AudioVisualizationState | null> {
    return this.audioVisualizationState.getObservable();
  }

  setAudioVisualization(_mode: VisualizationMode): void {
    console.warn('Audio visualization not supported');
  }

  private audioLevelsData = new ObservableState<AudioLevelsData | null>(null);
  getAudioLevelsDataObs(): Observable<AudioLevelsData | null> {
    return this.audioLevelsData.getObservable();
  }
}
function getPollDelayForItem(item: PlayableItemDto): number {
  let timeoutMs: number;
  const scheduledStart = item.liveRecordingInfo?.scheduledStartTime;
  const itemState = item.itemState;
  if (scheduledStart) {
    // start polling at the scheduled start time, and then
    // every second after that
    timeoutMs = Math.max(1000, scheduledStart - Date.now());
  } else if (itemState === 'error') {
    // Its a bit vague what exactly itemState 'error' means.
    // It could for example be due to some analytics failure unrelated to the
    // Item getting playable media, so we do retry but with a longer delay
    timeoutMs = 10_000;
  } else if (itemState === 'new') {
    // We want to start playing as soon as a placeholder gets media,
    // especially if the upload is done from this client.
    timeoutMs = item.hasUploadProgress ? 1000 : 5000;
  } else {
    timeoutMs = 2000;
  }

  return Math.min(timeoutMs, 15_000);
}

